import {
	useQuery,
	useInfiniteQuery,
	useQueryClient,
	useMutation,
} from "react-query";
import { toast } from "react-toastify";
import API from "./utils/api";
import {
	BonusGroupDto,
	BonusGroupPaginator,
	CreateBonusGroupDto,
	QueryBonusGroupDto,
	SearchBonusGroupDto,
	UpdateBonusGroupDto,
} from "../models/bonus-group.dto";
import ENDPOINTS from "./utils/endpoints";
import { QueryOptionsDto } from "../models/generic.dto";

export function useGetBonusGroup(
	companyId: number,
	options?: QueryBonusGroupDto,
) {
	const url = ENDPOINTS.PROCESSING_COMPANY.BONUSGROUP.GET;
	const request = (options: QueryBonusGroupDto) =>
		API.ProcessingCompanyAPI.getBonusGroups(companyId, options);

	const { data, isLoading, error, refetch } = useQuery<
		BonusGroupPaginator,
		Error
	>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetBonusGroupSearch(companyId: number) {
	return useMutation(
		(options: {
			q?: string;
			page?: number;
			limit?: number;
			orderBy?: string;
		}) =>
			API.ProcessingCompanyAPI.getBonusGroupSearch(
				companyId,
				options as SearchBonusGroupDto,
			),
		{
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetBonusGroupById(companyId: number, id: number) {
	const url = ENDPOINTS.PROCESSING_COMPANY.BONUSGROUP.DETAIL;
	const request = API.ProcessingCompanyAPI.getBonusGroupById(companyId, id);

	const { data, isLoading, error } = useQuery<{ data: BonusGroupDto }, Error>(
		[url, id],
		() => request,
		{
			enabled: !!id,
		},
	);

	return {
		data: data,
		isLoading,
		error,
	};
}

export function useCreateBonusGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			companyId,
			payload,
		}: {
			companyId: number;
			payload: CreateBonusGroupDto;
		}) => API.ProcessingCompanyAPI.createBonusGroup(companyId, payload),
		{
			onSuccess: () => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.BONUSGROUP.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUpdateBonusGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			payload,
			companyId,
			id,
		}: {
			payload: UpdateBonusGroupDto;
			companyId: number;
			id: number;
		}) => API.ProcessingCompanyAPI.updateBonusGroup(companyId, id, payload),
		{
			onSuccess: () => {
				toast.success("Record updated successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.BONUSGROUP.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useDeleteBonusGroup() {
	return useMutation(
		({ companyId, id }: { companyId: number; id: number }) =>
			API.ProcessingCompanyAPI.deleteBonusGroupById(companyId, id),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}